import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Spinner } from 'react-bootstrap';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';
import analyticsService from '../services/analyticsService';

function VisualMoodBoard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [moodBoardImages, setMoodBoardImages] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [archetype, setArchetype] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedMoodBoard, setSavedMoodBoard] = useState(null);
  const [regenerationCount, setRegenerationCount] = useState(0);
  const MAX_REGENERATIONS = 5;

  // Brand elements for summary display
  const [colorPalette, setColorPalette] = useState(null);
  const [textures, setTextures] = useState([]);
  const [styles, setStyles] = useState([]);
  const [promptDetails, setPromptDetails] = useState(null); // Store full prompt and negative prompt

  // Upload state
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  useEffect(() => {
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializePage = async () => {
    setLoading(true);

    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Fetch brand elements and saved mood board in parallel
    await Promise.all([
      fetchSavedMoodBoard(),
      fetchBrandElements()
    ]);

    setLoading(false);
  };

  const fetchBrandElements = async () => {
    try {
      // Fetch color palette
      const paletteResponse = await fetch('/api/brand/get-saved-palette', {
        method: 'GET',
        credentials: 'include'
      });
      if (paletteResponse.ok) {
        const paletteData = await paletteResponse.json();
        setColorPalette(paletteData.palette);
      }

      // Fetch textures
      const textureResponse = await fetch('/api/brand/get-saved-brand-texture', {
        method: 'GET',
        credentials: 'include'
      });
      if (textureResponse.ok) {
        const textureData = await textureResponse.json();
        if (textureData.brand_texture && textureData.brand_texture.selected_words) {
          setTextures(textureData.brand_texture.selected_words);
        }
      }

      // Fetch styles
      const styleResponse = await fetch('/api/brand/get-saved-brand-style', {
        method: 'GET',
        credentials: 'include'
      });
      if (styleResponse.ok) {
        const styleData = await styleResponse.json();
        if (styleData.brand_style && styleData.brand_style.selected_words) {
          setStyles(styleData.brand_style.selected_words);
        }
      }

      // Fetch archetype
      const archetypeResponse = await fetch('/api/brand/get-archetypes', {
        method: 'GET',
        credentials: 'include'
      });
      if (archetypeResponse.ok) {
        const archetypeData = await archetypeResponse.json();
        if (archetypeData.primary && archetypeData.primary.name) {
          setArchetype(archetypeData.primary.name);
        }
      }
    } catch (err) {
      console.log('Error fetching brand elements:', err);
    }
  };

  const fetchSavedMoodBoard = async () => {
    try {
      const response = await fetch('/api/brand/get-saved-mood-board', {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSavedMoodBoard(data.mood_board);
        setMoodBoardImages(data.mood_board.images);
        // Pre-select all saved images
        setSelectedImages(data.mood_board.images.map((_, idx) => idx));
      }
      // If 404, no saved mood board - that's okay
    } catch (err) {
      console.log('No saved mood board found or error fetching:', err);
    }
  };

  const handleGenerateMoodBoard = async () => {
    setGenerating(true);
    setError('');
    setSavedMoodBoard(null);
    setSelectedImages([]);
    setRegenerationCount(0);

    try {
      const response = await fetch('/api/brand/generate-mood-board', {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setMoodBoardImages(data.images);
        setArchetype(data.archetype);

        // Track mood board generation
        analyticsService.commandCenter.moodBoardGenerated(data.archetype, {
          image_count: data.images?.length || 0
        });

        // Extract prompt details from first image (all images have same prompts)
        if (data.images && data.images.length > 0 && data.images[0].full_prompt) {
          setPromptDetails({
            fullPrompt: data.images[0].full_prompt,
            negativePrompt: data.images[0].negative_prompt
          });
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to generate mood board');
      }
    } catch (err) {
      setError('Error generating mood board: ' + err.message);
    }

    setGenerating(false);
  };

  const toggleImageSelection = (index) => {
    if (selectedImages.includes(index)) {
      setSelectedImages(selectedImages.filter(i => i !== index));
    } else {
      if (selectedImages.length >= 6) {
        setError('You can select up to 6 images');
        setTimeout(() => setError(''), 3000);
        return;
      }
      setSelectedImages([...selectedImages, index]);
    }
  };

  const handleRegenerateUnselected = async () => {
    if (regenerationCount >= MAX_REGENERATIONS) {
      setError(`Maximum ${MAX_REGENERATIONS} regenerations reached. Please save your selection.`);
      return;
    }

    setGenerating(true);
    setError('');

    try {
      // Calculate how many images to generate (unselected count)
      const numToGenerate = moodBoardImages.length - selectedImages.length;

      if (numToGenerate === 0) {
        setError('Please deselect some images to regenerate them');
        setGenerating(false);
        return;
      }

      const response = await fetch('/api/brand/generate-mood-board', {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const newImages = data.images;

        // Merge selected images with new ones
        const mergedImages = [];
        let newImageIndex = 0;

        for (let i = 0; i < moodBoardImages.length; i++) {
          if (selectedImages.includes(i)) {
            // Keep selected image
            mergedImages.push(moodBoardImages[i]);
          } else {
            // Replace with new image
            if (newImageIndex < newImages.length) {
              mergedImages.push(newImages[newImageIndex]);
              newImageIndex++;
            }
          }
        }

        setMoodBoardImages(mergedImages);
        setRegenerationCount(regenerationCount + 1);

        // Update prompt details from new images
        if (newImages && newImages.length > 0 && newImages[0].full_prompt) {
          setPromptDetails({
            fullPrompt: newImages[0].full_prompt,
            negativePrompt: newImages[0].negative_prompt
          });
        }

        // Clear selection after regeneration
        setSelectedImages([]);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to regenerate mood board images');
      }
    } catch (err) {
      setError('Error regenerating mood board: ' + err.message);
    }

    setGenerating(false);
  };

  const handleSaveMoodBoard = async () => {
    if (!moodBoardImages || moodBoardImages.length === 0) return;

    // If no images selected, save all
    const imagesToSave = selectedImages.length > 0
      ? selectedImages.map(idx => moodBoardImages[idx])
      : moodBoardImages;

    setSaving(true);
    setError('');

    try {
      const response = await fetch('/api/brand/save-mood-board', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          mood_board: {
            images: imagesToSave,
            archetype: archetype,
            saved_at: new Date().toISOString()
          }
        })
      });

      if (response.ok) {
        navigate('/dashboard/branding');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save mood board');
      }
    } catch (err) {
      setError('Error saving mood board: ' + err.message);
    }

    setSaving(false);
  };

  const validateFiles = (files) => {
    const allowed = ['png', 'jpg', 'jpeg', 'svg'];
    const maxSize = 2.5 * 1024 * 1024;
    const maxFiles = 6;

    if (files.length > maxFiles) {
      return `Maximum ${maxFiles} files allowed`;
    }
    for (const file of files) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!allowed.includes(ext)) {
        return `"${file.name}" is not allowed. Use PNG, JPEG, or SVG.`;
      }
      if (file.size > maxSize) {
        return `"${file.name}" is too large. Maximum 2.5 MB per file.`;
      }
    }
    return null;
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    const err = validateFiles(files);
    if (err) {
      setUploadError(err);
      return;
    }
    setUploadError('');
    setUploadFiles(files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;
    const err = validateFiles(files);
    if (err) {
      setUploadError(err);
      return;
    }
    setUploadError('');
    setUploadFiles(files);
  };

  const handleUploadImages = async () => {
    if (uploadFiles.length === 0) return;
    setUploading(true);
    setUploadError('');

    try {
      const formData = new FormData();
      uploadFiles.forEach(file => formData.append('images', file));

      const response = await fetch('/api/brand/upload-mood-board-images', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setMoodBoardImages(prev => prev ? [...prev, ...data.images] : data.images);
        setUploadFiles([]);
      } else {
        const errorData = await response.json();
        setUploadError(errorData.error || 'Upload failed');
      }
    } catch (err) {
      setUploadError('Error uploading images: ' + err.message);
    }

    setUploading(false);
  };

  const hexToSimpleColor = (hex) => {
    if (!hex || typeof hex !== 'string') return null;
    hex = hex.replace('#', '').toLowerCase();
    if (hex.length !== 6) return null;
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2 / 255;

    if (max - min < 30) {
      if (l < 0.15) return 'black';
      if (l > 0.85) return 'white';
      return 'gray';
    }
    // Determine hue-based name
    if (r >= max && g < 80 && b < 80) return 'red';
    if (r > 180 && g > 100 && g < 180 && b < 80) return 'orange';
    if (r > 180 && g > 180 && b < 100) return 'gold';
    if (g >= max && r < 100 && b < 100) return 'green';
    if (b >= max && r < 100 && g < 100) return 'blue';
    if (b >= max && r < 100) return 'teal';
    if (r > 100 && b > 100 && g < 80) return 'purple';
    if (r > 150 && g < 100 && b > 80) return 'burgundy';
    if (r > 180 && g > 150 && b > 100) return 'tan';
    if (r > 150 && g > 80 && b < 60) return 'brown';
    if (l < 0.25) return 'dark';
    if (l > 0.75) return 'light';
    return null;
  };

  const buildPinterestQuery = () => {
    const parts = [];
    if (archetype) parts.push(`${archetype} archetype`);
    if (colorPalette) {
      const hexes = [
        colorPalette.primary,
        colorPalette.secondary,
        colorPalette.accent1,
        colorPalette.accent2,
        colorPalette.accent3
      ];
      const simpleNames = [...new Set(hexes.map(hexToSimpleColor).filter(Boolean))];
      if (simpleNames.length > 0) parts.push(simpleNames.join(', '));
    }
    if (styles.length > 0) parts.push(styles.join(', '));
    if (textures.length > 0) parts.push(textures.join(', '));
    return parts.join(', ') || 'brand mood board';
  };

  const pinterestSearchUrl = () => {
    return `https://www.pinterest.com/search/pins/?q=${encodeURIComponent(buildPinterestQuery())}`;
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Loading...</p>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <BackToDashboard module="branding" />
      <Row className="justify-content-center">
        <Col md={12} lg={10}>
          <Card className="shadow">
            <Card.Body className="p-5">
              <h1 className="text-center mb-4">🎨 Visual Mood Board</h1>

              {archetype && (
                <Alert variant="info" className="text-center mb-4">
                  <strong>Brand Archetype:</strong> {archetype}
                </Alert>
              )}

              {/* Brand Elements Summary */}
              {(colorPalette || textures.length > 0 || styles.length > 0) && (
                <Card className="mb-4" style={{ backgroundColor: '#f8f9fa', border: '2px solid #dee2e6' }}>
                  <Card.Body>
                    <h6 className="mb-3">Your Brand Elements</h6>
                    <p className="text-muted small mb-3">
                      These elements are used to generate your mood board. Click Edit to modify any of them.
                    </p>

                    <Row className="g-3">
                      {/* Color Palette */}
                      {colorPalette && (
                        <Col md={4}>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <strong className="small">Color Palette:</strong>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 text-decoration-none"
                              onClick={() => navigate('/dashboard/palette-customize')}
                              style={{ fontSize: '0.85rem' }}
                            >
                              Edit
                            </Button>
                          </div>
                          <div className="d-flex gap-2 flex-wrap">
                            {/* Primary and Secondary (larger swatches) */}
                            {colorPalette.primary && (
                              <div
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  backgroundColor: colorPalette.primary,
                                  borderRadius: '4px',
                                  border: '2px solid #fff',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                                title={`Primary: ${colorPalette.primary}`}
                              />
                            )}
                            {colorPalette.secondary && (
                              <div
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  backgroundColor: colorPalette.secondary,
                                  borderRadius: '4px',
                                  border: '2px solid #fff',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                                title={`Secondary: ${colorPalette.secondary}`}
                              />
                            )}
                            {/* Accent colors (smaller circles) */}
                            {colorPalette.accent1 && (
                              <div
                                style={{
                                  width: '30px',
                                  height: '30px',
                                  backgroundColor: colorPalette.accent1,
                                  borderRadius: '50%',
                                  border: '2px solid #fff',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                  marginTop: '5px'
                                }}
                                title={`${colorPalette.accent1_name || 'Accent 1'}: ${colorPalette.accent1}`}
                              />
                            )}
                            {colorPalette.accent2 && (
                              <div
                                style={{
                                  width: '30px',
                                  height: '30px',
                                  backgroundColor: colorPalette.accent2,
                                  borderRadius: '50%',
                                  border: '2px solid #fff',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                  marginTop: '5px'
                                }}
                                title={`${colorPalette.accent2_name || 'Accent 2'}: ${colorPalette.accent2}`}
                              />
                            )}
                            {colorPalette.accent3 && (
                              <div
                                style={{
                                  width: '30px',
                                  height: '30px',
                                  backgroundColor: colorPalette.accent3,
                                  borderRadius: '50%',
                                  border: '2px solid #fff',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                  marginTop: '5px'
                                }}
                                title={`${colorPalette.accent3_name || 'Accent 3'}: ${colorPalette.accent3}`}
                              />
                            )}
                          </div>
                        </Col>
                      )}

                      {/* Textures */}
                      {textures.length > 0 && (
                        <Col md={4}>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <strong className="small">Textures:</strong>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 text-decoration-none"
                              onClick={() => navigate('/brand-texture')}
                              style={{ fontSize: '0.85rem' }}
                            >
                              Edit
                            </Button>
                          </div>
                          <div className="small">
                            {textures.join(', ')}
                          </div>
                        </Col>
                      )}

                      {/* Styles */}
                      {styles.length > 0 && (
                        <Col md={4}>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <strong className="small">Styles:</strong>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 text-decoration-none"
                              onClick={() => navigate('/brand-style')}
                              style={{ fontSize: '0.85rem' }}
                            >
                              Edit
                            </Button>
                          </div>
                          <div className="small">
                            {styles.join(', ')}
                          </div>
                        </Col>
                      )}
                    </Row>
                  </Card.Body>
                </Card>
              )}

              {/* Pinterest Search Link */}
              {(archetype || colorPalette || styles.length > 0 || textures.length > 0) && (
                <Card className="mb-4" style={{ backgroundColor: '#fdf6e8', border: '2px solid #cca557' }}>
                  <Card.Body>
                    <h6 className="mb-2">Search Pinterest for Inspiration</h6>
                    <p className="text-muted small mb-2">
                      Browse Pinterest for images that match your brand aesthetic, then screenshot your favorites and upload them below.
                    </p>
                    <div
                      className="small p-2 rounded mb-3"
                      style={{
                        backgroundColor: 'white',
                        border: '1px solid #dee2e6',
                        fontFamily: 'monospace',
                        fontSize: '0.85rem'
                      }}
                    >
                      {buildPinterestQuery()}
                    </div>
                    <a
                      href={pinterestSearchUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm"
                      style={{
                        backgroundColor: '#E60023',
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    >
                      Open Pinterest Search
                    </a>
                  </Card.Body>
                </Card>
              )}

              {/* Upload Your Own Images */}
              <Card className="mb-4" style={{ border: '2px dashed #cca557' }}>
                <Card.Body>
                  <h6 className="mb-2">Upload Your Own Images</h6>
                  <p className="text-muted small mb-3">
                    Upload screenshots from Pinterest boards, your own photos, or any inspiration images. They'll appear alongside AI-generated images in the gallery below.
                  </p>

                  {uploadError && <Alert variant="danger" className="py-2">{uploadError}</Alert>}

                  <div
                    onDrop={handleDrop}
                    onDragOver={(e) => e.preventDefault()}
                    onClick={() => document.getElementById('mood-board-upload').click()}
                    style={{
                      border: '2px dashed #dee2e6',
                      borderRadius: '8px',
                      padding: '24px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      backgroundColor: '#fafafa',
                      transition: 'border-color 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.borderColor = '#cca557'}
                    onMouseOut={(e) => e.currentTarget.style.borderColor = '#dee2e6'}
                  >
                    <input
                      id="mood-board-upload"
                      type="file"
                      multiple
                      accept=".png,.jpg,.jpeg,.svg"
                      style={{ display: 'none' }}
                      onChange={handleFileSelect}
                    />
                    <div style={{ fontSize: '2rem', marginBottom: '8px' }}>+</div>
                    <div className="small text-muted">
                      Click or drag images here (PNG, JPEG, SVG — max 2.5 MB each, up to 6 files)
                    </div>
                  </div>

                  {uploadFiles.length > 0 && (
                    <div className="mt-3">
                      <Row className="g-2 mb-3">
                        {uploadFiles.map((file, idx) => (
                          <Col xs={4} md={2} key={idx}>
                            <img
                              src={URL.createObjectURL(file)}
                              alt={file.name}
                              style={{
                                width: '100%',
                                height: '80px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                border: '2px solid #cca557'
                              }}
                            />
                            <div className="text-muted text-center" style={{ fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {file.name}
                            </div>
                          </Col>
                        ))}
                      </Row>
                      <div className="d-flex gap-2">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleUploadImages}
                          disabled={uploading}
                        >
                          {uploading ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-1" />
                              Uploading...
                            </>
                          ) : (
                            `Upload ${uploadFiles.length} Image${uploadFiles.length > 1 ? 's' : ''}`
                          )}
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => { setUploadFiles([]); setUploadError(''); }}
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>

              {/* Prompt Details Display */}
              {promptDetails && (
                <Card className="mb-4" style={{ backgroundColor: '#e8f4f8', border: '2px solid #bee5eb' }}>
                  <Card.Body>
                    <h6 className="mb-3">AI Generation Details</h6>
                    <p className="text-muted small mb-3">
                      This shows what instructions were sent to Ideogram AI to generate your mood board images.
                    </p>

                    <div className="mb-3">
                      <strong className="small d-block mb-1">Positive Prompt (what to include):</strong>
                      <div
                        className="small p-2 rounded"
                        style={{
                          backgroundColor: 'white',
                          border: '1px solid #dee2e6',
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                          whiteSpace: 'pre-wrap'
                        }}
                      >
                        {promptDetails.fullPrompt}
                      </div>
                    </div>

                    <div>
                      <strong className="small d-block mb-1">Negative Prompt (what to avoid):</strong>
                      <div
                        className="small p-2 rounded"
                        style={{
                          backgroundColor: 'white',
                          border: '1px solid #dee2e6',
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                          whiteSpace: 'pre-wrap'
                        }}
                      >
                        {promptDetails.negativePrompt}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              )}

              {error && <Alert variant="danger">{error}</Alert>}

              {savedMoodBoard && moodBoardImages && (
                <Alert variant="success" className="text-center mb-4">
                  <strong>✓ Your Saved Mood Board</strong> - These are your saved inspiration images. You can regenerate to see new options.
                </Alert>
              )}

              {!moodBoardImages ? (
                <div className="text-center py-5">
                  <p className="lead mb-4">
                    Generate AI-powered mood board images that capture your brand's aesthetic, energy, and personality.
                  </p>
                  <p className="text-muted mb-4">
                    Claude will create 6 unique visual inspiration images based on your brand archetype,
                    color palette, typography, and overall design direction.
                  </p>
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleGenerateMoodBoard}
                    disabled={generating}
                  >
                    {generating ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Generating Mood Board...
                      </>
                    ) : (
                      'Generate Mood Board →'
                    )}
                  </Button>
                </div>
              ) : (
                <>
                  {generating && (
                    <Alert variant="info" className="text-center">
                      <Spinner animation="border" size="sm" className="me-2" />
                      <strong>Generating images...</strong> Creating visual inspiration for your brand.
                    </Alert>
                  )}

                  <div style={{ opacity: generating ? 0.5 : 1, transition: 'opacity 0.3s' }}>
                    <p className="text-center mb-4">
                      <strong>Click images to select your favorites.</strong> You can select up to 6 images, then click "Save & Continue" below, or regenerate unselected ones for more options.
                    </p>

                    <Row>
                      {moodBoardImages.map((image, idx) => {
                        const isSelected = selectedImages.includes(idx);
                        return (
                          <Col md={6} lg={4} key={idx} className="mb-4">
                            <Card
                              style={{
                                cursor: 'pointer',
                                border: isSelected ? '4px solid #28a745' : '2px solid #ddd',
                                backgroundColor: isSelected ? '#d4edda' : 'white',
                                position: 'relative',
                                transition: 'all 0.2s ease'
                              }}
                              onClick={() => toggleImageSelection(idx)}
                            >
                              {isSelected && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '10px',
                                    right: '10px',
                                    backgroundColor: '#28a745',
                                    color: 'white',
                                    borderRadius: '50%',
                                    width: '30px',
                                    height: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    zIndex: 10
                                  }}
                                >
                                  ✓
                                </div>
                              )}
                              <a
                                href={image.base64 ? `data:image/png;base64,${image.base64}` : `http://localhost:8000${image.url}`}
                                download={`mood-board-${idx + 1}.png`}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  position: 'absolute',
                                  top: '10px',
                                  left: '10px',
                                  backgroundColor: '#007bff',
                                  color: 'white',
                                  borderRadius: '4px',
                                  padding: '6px 10px',
                                  fontSize: '14px',
                                  textDecoration: 'none',
                                  zIndex: 10,
                                  fontWeight: 'bold'
                                }}
                                title="Download this image"
                              >
                                ⬇
                              </a>
                              <Card.Img
                                variant="top"
                                src={image.base64 ? `data:image/png;base64,${image.base64}` : `http://localhost:8000${image.url}`}
                                style={{
                                  height: '250px',
                                  objectFit: 'cover'
                                }}
                              />
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>

                    {selectedImages.length > 0 && (
                      <Alert variant="primary" className="mt-3">
                        <strong>{selectedImages.length} image(s) selected</strong> - These will be kept when you regenerate.
                      </Alert>
                    )}

                    {regenerationCount > 0 && (
                      <Alert variant="info" className="mt-3">
                        Regenerations used: {regenerationCount} / {MAX_REGENERATIONS}
                      </Alert>
                    )}
                  </div>

                  <div className="d-flex justify-content-between mt-4">
                    <div>
                      <Button
                        variant="outline-secondary"
                        onClick={handleGenerateMoodBoard}
                        disabled={generating}
                        className="me-2"
                      >
                        {generating ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Generating...
                          </>
                        ) : (
                          '🔄 Start Over'
                        )}
                      </Button>
                      {selectedImages.length > 0 && selectedImages.length < moodBoardImages.length && (
                        <Button
                          variant="outline-primary"
                          onClick={handleRegenerateUnselected}
                          disabled={generating || regenerationCount >= MAX_REGENERATIONS}
                        >
                          {generating ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Regenerating...
                            </>
                          ) : (
                            `🎲 Regenerate ${moodBoardImages.length - selectedImages.length} Unselected`
                          )}
                        </Button>
                      )}
                    </div>
                    <Button
                      variant="primary"
                      size="lg"
                      disabled={saving}
                      onClick={handleSaveMoodBoard}
                    >
                      {saving ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : (
                        'Save & Continue →'
                      )}
                    </Button>
                  </div>
                </>
              )}

              {!moodBoardImages && (
                <div className="text-center mt-4">
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/dashboard/branding')}
                  >
                    ← Back to Dashboard
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default VisualMoodBoard;
